import {
  collection,
  documentId,
  endAt,
  limit as firestoreLimit,
  orderBy,
  query as firestoreQuery,
  startAt,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { IS_DEV_UI_ENABLED, devError } from "./devUi";
import { getDocsCached } from "./firestoreReadCache";
import { quoteSearchPlan, quoteSearchMatches } from "./quoteSearchContract";

const DEFAULT_TTL_MS = 5 * 60 * 1000;
const DEFAULT_LIMIT = 24;
const MAX_LIMIT = 30;
const isDevRuntime = IS_DEV_UI_ENABLED;
export const CUSTOMER_SEARCH_INDEX_COLLECTION = "customer_search_index";
const CUSTOMER_PERSON_PREFIX_LIMIT = 200;
const CUSTOMER_PERSON_KEYWORD_LIMIT = 100;
const CUSTOMER_PERSON_SEARCH_TEXT_LIMIT = 3000;

export const normalizeSearchText = (value = "") =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s@._-]+/g, " ")
    .replace(/\s+/g, " ");

export const normalizePhone = (value = "") => {
  const digits = String(value ?? "").replace(/\D+/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const normalizeCompact = (value = "") => normalizeSearchText(value).replace(/[^a-z0-9]+/g, "");

const normalizeEmailKey = (value = "") =>
  normalizeSearchText(value).replace(/\s+/g, "");

const uniqueLimited = (values = [], max = 100) => {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const normalized = String(value ?? "").trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result.slice(0, max);
};

const addWordPrefixes = (prefixes, value, maxTokenLength = 24) => {
  normalizeSearchText(value)
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const capped = token.slice(0, maxTokenLength);
      for (let index = 1; index <= capped.length; index += 1) {
        prefixes.add(capped.slice(0, index));
        if (prefixes.size >= CUSTOMER_PERSON_PREFIX_LIMIT) return;
      }
    });
};

const addPhonePrefixes = (prefixes, value) => {
  const phone = normalizePhone(value);
  if (!phone) return;
  for (let index = 1; index <= phone.length; index += 1) {
    prefixes.add(phone.slice(0, index));
    if (prefixes.size >= CUSTOMER_PERSON_PREFIX_LIMIT) return;
  }
};

const resolvePersonDisplayName = (record = {}) =>
  String(
    record.customerSnapshot?.customerName ||
      record.customerCompanyName ||
      record.companyName ||
      record.customerName ||
      record.name ||
      record.displayName ||
      record.contact?.name ||
      record.crmContact?.name ||
      record.quoteFor?.company ||
      record.generalQuote?.quoteFor?.company ||
      record.quoteFor?.name ||
      record.generalQuote?.quoteFor?.name ||
      record.companyOrProject ||
      ""
  ).trim();

export const buildCustomerSearchIndexId = (record = {}) => {
  const phone = normalizePhone(
    record.normalizedPhone ||
      record.phone ||
      record.whatsapp ||
      record.customerPhone ||
      record.customerWhatsapp ||
      record.contact?.phone ||
      record.contact?.whatsapp ||
      record.crmContact?.phone ||
      record.crmContact?.whatsapp ||
      record.quoteFor?.phone ||
      record.generalQuote?.quoteFor?.phone ||
      ""
  );
  if (phone) return `phone_${phone}`;

  const email = normalizeEmailKey(
    record.normalizedEmail ||
      record.email ||
      record.customerEmail ||
      record.contact?.email ||
      record.crmContact?.email ||
      record.quoteFor?.email ||
      record.generalQuote?.quoteFor?.email ||
      ""
  );
  if (email) return `email_${email.slice(0, 120)}`;

  const nameKey = normalizeCompact(resolvePersonDisplayName(record)).slice(0, 90);
  if (nameKey) return `name_${nameKey}`;

  const customerId = String(record.customerId || record.customerKey || "").trim();
  if (customerId) return `customer_${customerId}`;

  const crmLeadId = String(record.crmLeadId || record.leadId || "").trim();
  if (crmLeadId) return `lead_${crmLeadId}`;

  return "";
};

export const buildCustomerSearchIndexRecord = (record = {}, options = {}) => {
  const sourceType = String(options.sourceType || record.sourceType || "customer").trim();
  const displayName = resolvePersonDisplayName(record);
  const phone = String(
    record.phone ||
      record.customerPhone ||
      record.contact?.phone ||
      record.crmContact?.phone ||
      record.quoteFor?.phone ||
      record.generalQuote?.quoteFor?.phone ||
      ""
  ).trim();
  const whatsapp = String(
    record.whatsapp ||
      record.customerWhatsapp ||
      record.contact?.whatsapp ||
      record.crmContact?.whatsapp ||
      ""
  ).trim();
  const email = String(
    record.email ||
      record.customerEmail ||
      record.contact?.email ||
      record.crmContact?.email ||
      record.quoteFor?.email ||
      record.generalQuote?.quoteFor?.email ||
      ""
  ).trim();
  const companyName = String(
    record.companyName ||
      record.companyOrProject ||
      record.customerCompanyName ||
      record.customerSnapshot?.customerName ||
      record.quoteFor?.company ||
      ""
  ).trim();
  const address = String(
    record.address ||
      record.customerAddress ||
      record.siteAddress ||
      record.contact?.address ||
      record.crmContact?.address ||
      record.quoteFor?.address ||
      record.generalQuote?.quoteFor?.address ||
      ""
  ).trim();
  const siteName = String(
    record.siteName ||
      record.projectSiteName ||
      record.generalQuote?.siteName ||
      ""
  ).trim();
  const siteAddress = String(
    record.siteAddress ||
      record.siteLocationAddress ||
      record.generalQuote?.siteAddress ||
      ""
  ).trim();
  const quoteDisplayTitle = String(
    record.quoteDisplayTitle ||
      record.generalQuote?.quoteDisplayTitle ||
      ""
  ).trim();
  const quoteNo = String(record.quoteNo || record.latestQuoteNo || record.primaryQuoteNo || "").trim();
  const stageLabel = String(
    record.stageLabel ||
      record.stageNameSnapshot ||
      record.crmStageLabel ||
      record.stageId ||
      record.workStage ||
      record.status ||
      ""
  ).trim();
  const normalizedPhone = normalizePhone(
    record.normalizedPhone || phone || whatsapp || record.customerPhone || ""
  );
  const normalizedWhatsapp = normalizePhone(record.normalizedWhatsapp || whatsapp || phone || "");
  const normalizedEmail = normalizeEmailKey(record.normalizedEmail || email || "");
  const displayNameLower = normalizeSearchText(displayName);
  const companyNameLower = normalizeSearchText(companyName);
  const addressLower = normalizeSearchText(address);
  const searchPieces = uniqueLimited(
    [
      displayName,
      phone,
      whatsapp,
      normalizedPhone,
      normalizedWhatsapp,
      email,
      normalizedEmail,
      companyName,
      address,
      siteName,
      siteAddress,
      quoteDisplayTitle,
      record.source,
      quoteNo,
      record.quoteId,
      record.crmLeadId,
      record.customerId,
      record.leadId,
      record.title,
      stageLabel,
      ...(Array.isArray(record.tags) ? record.tags : []),
    ].filter(Boolean),
    80
  );
  const searchText = normalizeSearchText(searchPieces.join(" ")).slice(
    0,
    CUSTOMER_PERSON_SEARCH_TEXT_LIMIT
  );
  const searchKeywords = uniqueLimited(
    searchText
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 2),
    CUSTOMER_PERSON_KEYWORD_LIMIT
  );
  const prefixSet = new Set();
  const phonePrefixSet = new Set();
  [
    displayName,
    companyName,
    siteName,
    siteAddress,
    quoteDisplayTitle,
    email,
    normalizedEmail,
    record.source,
    quoteNo,
    stageLabel,
  ].forEach((value) => addWordPrefixes(prefixSet, value));
  [phone, whatsapp, normalizedPhone, normalizedWhatsapp].forEach((value) => {
    addPhonePrefixes(phonePrefixSet, value);
    addWordPrefixes(prefixSet, normalizePhone(value));
  });

  const quoteId = String(record.quoteId || record.id || record.sourceQuoteId || "").trim();
  const sourceQuoteId = String(record.sourceQuoteId || record.fullQuoteId || quoteId || "").trim();
  const quoteIndexId = String(
    record.quoteIndexId || record.indexId || record.indexDocId || quoteId || ""
  ).trim();
  const sourcePath = String(record.sourcePath || record.fullQuotePath || "").trim();
  const customerId = String(record.customerId || record.customerKey || "").trim();
  const crmLeadId = String(record.crmLeadId || record.leadId || "").trim();
  const latestQuoteTotal = Number(
    record.latestQuoteTotal ||
      record.latestQuoteValue ||
      record.totalAmount ||
      record.grandTotal ||
      record.finalTotal ||
      record.finalValue ||
      0
  );
  const sourceTypes = uniqueLimited(
    [sourceType, ...(Array.isArray(record.sourceTypes) ? record.sourceTypes : [])],
    8
  );
  const hasCustomer =
    record.hasCustomer === true || sourceTypes.includes("customer") || Boolean(customerId);
  const hasLead =
    record.hasLead === true || sourceTypes.includes("crm_lead") || Boolean(crmLeadId);
  const hasQuotes =
    record.hasQuotes === true ||
    sourceTypes.includes("quote_customer") ||
    Boolean(quoteNo || quoteId || record.latestQuoteId);
  const latestQuoteDate =
    record.latestQuoteDate ||
    record.quoteDate ||
    record.date ||
    record.confirmedAt ||
    record.updatedAt ||
    record.createdAt ||
    null;

  return {
    id: buildCustomerSearchIndexId({
      ...record,
      sourceType,
      displayName,
      phone,
      whatsapp,
      email,
      normalizedPhone,
      normalizedEmail,
      customerId,
      crmLeadId,
      quoteId,
    }),
    personId: buildCustomerSearchIndexId({
      ...record,
      sourceType,
      displayName,
      phone,
      whatsapp,
      email,
      normalizedPhone,
      normalizedEmail,
      customerId,
      crmLeadId,
      quoteId,
    }),
    workspaceUid: options.workspaceUid || record.workspaceUid || "",
    displayName,
    customerName: displayName,
    displayNameLower,
    normalizedName: displayNameLower,
    nameNormalized: displayNameLower,
    customerNameLower: displayNameLower,
    phone,
    whatsapp,
    email,
    normalizedPhone,
    phoneNormalized: normalizedPhone,
    normalizedWhatsapp,
    normalizedEmail,
    emailCanonical: normalizedEmail,
    companyName,
    companyNameLower,
    address,
    addressLower,
    siteName,
    siteNameLower: normalizeSearchText(siteName),
    siteAddress,
    quoteDisplayTitle,
    quoteDisplayTitleLower: normalizeSearchText(quoteDisplayTitle),
    source: String(record.source || "").trim(),
    sourceType,
    sourceTypes,
    sourceSummary: {
      hasCustomer,
      hasLead,
      hasQuotes,
    },
    hasCustomer,
    hasLead,
    hasQuotes,
    customerId,
    customerKey: String(record.customerKey || customerId || "").trim(),
    crmLeadId,
    leadId: crmLeadId,
    quoteId,
    sourceQuoteId,
    fullQuoteId: sourceQuoteId,
    quoteIndexId,
    indexId: quoteIndexId,
    sourcePath,
    latestQuoteId: String(record.latestQuoteId || quoteId || "").trim(),
    latestQuoteNo: quoteNo,
    latestQuoteDate,
    latestQuoteStage: record.latestQuoteStage || record.stageLabel || record.workStage || "",
    latestQuoteType: record.latestQuoteType || record.quoteType || record.quotationType || "",
    latestQuoteTotal: Number.isFinite(latestQuoteTotal) ? latestQuoteTotal : 0,
    quoteCount: Number(record.quoteCount || record.totals?.totalQuotes || (quoteNo || quoteId ? 1 : 0)),
    quoteNo,
    quoteType: record.quoteType || record.quoteTypeResolved || "",
    quotationType: record.quotationType || record.quoteType || record.quoteTypeResolved || "",
    rowStorageMode: record.rowStorageMode || "",
    rowChunkCount: Number(record.rowChunkCount || 0),
    grandTotal: Number.isFinite(latestQuoteTotal) ? latestQuoteTotal : 0,
    status: record.status || "",
    leadStage: record.leadStage || record.stageLabel || record.stageId || record.workStage || "",
    customerStatus: record.customerStatus || record.status || "",
    stageId: record.stageId || record.workStage || "",
    stageLabel,
    totalAmount: Number.isFinite(latestQuoteTotal) ? latestQuoteTotal : 0,
    active: record.active !== false && record.deleted !== true,
    lastActivityAt: record.lastActivityAt || latestQuoteDate || record.updatedAt || record.createdAt || null,
    lastActivityLabel: record.lastActivityLabel || (hasQuotes ? "Latest quote" : hasLead ? "Lead activity" : "Customer activity"),
    searchText,
    searchKeywords,
    searchTokens: searchKeywords,
    searchPrefixes: Array.from(prefixSet).slice(0, CUSTOMER_PERSON_PREFIX_LIMIT),
    phonePrefixes: Array.from(phonePrefixSet).slice(0, CUSTOMER_PERSON_PREFIX_LIMIT),
  };
};

const safeLimit = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_LIMIT;
  return Math.min(MAX_LIMIT, Math.max(1, Math.floor(parsed)));
};

const startsWithRangeQuery = (collectionRef, fieldName, value, maxResults) =>
  firestoreQuery(
    collectionRef,
    orderBy(fieldName),
    startAt(value),
    endAt(`${value}\uf8ff`),
    firestoreLimit(maxResults)
  );

const arrayContainsQuery = (collectionRef, fieldName, value, maxResults) =>
  firestoreQuery(
    collectionRef,
    where(fieldName, "array-contains", value),
    firestoreLimit(maxResults)
  );

const makeCacheKey = (workspaceUid, collectionName, label, queryValue, maxResults) =>
  `users/${workspaceUid}/${collectionName}?globalSearch=${label}:${encodeURIComponent(
    queryValue
  )}&limit=${maxResults}`;

const readQuery = async ({
  queryRef,
  label,
  cacheKey,
  ttlMs = DEFAULT_TTL_MS,
  resultMap,
  source,
  collectionName,
}) => {
  try {
    const snap = await getDocsCached(queryRef, {
      label,
      cacheKey,
      ttlMs,
    });
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data() || {};
      if (
        collectionName === CUSTOMER_SEARCH_INDEX_COLLECTION &&
        (data.active === false || data.deleted === true || data.archived === true)
      ) {
        return;
      }
      if (!resultMap.has(docSnap.id)) {
        resultMap.set(docSnap.id, {
          id: docSnap.id,
          ...data,
          _searchSource: source,
        });
      }
    });
    return snap.size;
  } catch (error) {
    if (isDevRuntime) {
      devError("GLOBAL SEARCH QUERY FAILED", {
        label,
        collectionName,
        source,
        cacheKey,
        code: error?.code,
        message: error?.message,
      });
    }
    throw error;
  }
};

const runSearchQueries = async ({
  workspaceUid,
  collectionName,
  queryText,
  limit: requestedLimit,
  labelPrefix,
  builders,
  ttlMs,
  pagination,
  cacheScope = "",
}) => {
  const normalizedQuery = normalizeSearchText(queryText);
  const compactQuery = normalizeCompact(queryText);
  const normalizedPhone = normalizePhone(queryText);
  const maxResults = safeLimit(requestedLimit);

  if (!workspaceUid || normalizedQuery.length < 2) return [];

  const collectionRef = collection(db, "users", workspaceUid, collectionName);
  const resultMap = new Map();
  let jobs = builders({
    collectionRef,
    normalizedQuery,
    compactQuery,
    normalizedPhone,
    rawQuery: String(queryText || "").trim(),
    maxResults,
  }).filter(Boolean);

  // A complete modern quotation number is an exact indexed lookup, not a
  // customer/site/token waterfall. Keep both historical number aliases.
  if (!pagination && collectionName === "quote_index" && /^ac-\d{4}-\d+$/i.test(String(queryText).trim())) {
    const quoteNo = String(queryText).trim().toUpperCase();
    jobs = ["quoteNo", "quoteNoString"].map(field => ({ source: field, queryValue: quoteNo,
      queryRef: firestoreQuery(collectionRef, where(field, "==", quoteNo), firestoreLimit(maxResults)) }));
  }

  if (pagination) {
    const queryKey = `${workspaceUid}:${collectionName}:${normalizedQuery}:${cacheScope}`;
    if (pagination.queryKey && pagination.queryKey !== queryKey) throw new Error("Search changed. Start a new search page.");
    let jobIndex = pagination.jobIndex || 0;
    let cursor = pagination.cursor || null;
    let jobsRead = 0;
    while (jobIndex < jobs.length && jobsRead < 3) {
      const entry = jobs[jobIndex];
      const snap = await getDocsCached(cursor ? firestoreQuery(entry.queryRef, startAfter(cursor)) : entry.queryRef, {
        label: `${labelPrefix}.${entry.source}.page`,
        cacheKey: `${makeCacheKey(workspaceUid, collectionName, entry.source, entry.queryValue, maxResults)}:scope=${cacheScope}:after=${cursor?.id || "start"}`,
        ttlMs,
      });
      jobsRead += 1;
      const records = snap.docs.map((item) => ({ ...item.data(), id: item.id, _searchSource: entry.source }));
      if (snap.size === maxResults) cursor = snap.docs[snap.docs.length - 1];
      else { jobIndex += 1; cursor = null; }
      if (records.length || jobIndex === jobs.length) {
        return { records, hasMore: jobIndex < jobs.length,
          pageState: { queryKey, jobIndex, cursor } };
      }
    }
    return { records: [], hasMore: jobIndex < jobs.length, pageState: { queryKey, jobIndex, cursor } };
  }

  for (const entry of jobs) {
    if (resultMap.size >= maxResults) break;
    await readQuery({
        ...entry,
        label: `${labelPrefix}.${entry.source}`,
        cacheKey:
          entry.cacheKey ||
          `${makeCacheKey(workspaceUid, collectionName, entry.source, entry.queryValue, maxResults)}${cacheScope ? `:scope=${cacheScope}` : ""}`,
        ttlMs,
        resultMap,
        collectionName,
    });
  }

  return Array.from(resultMap.values()).slice(0, maxResults);
};

export const searchQuotations = (workspaceUid, queryText, options = {}) =>
  runSearchQueries({
    workspaceUid, collectionName: "quote_index", queryText, limit: options.limit,
    pagination: options.pagination, cacheScope: options.cacheScope,
    ttlMs: options.ttlMs || DEFAULT_TTL_MS, labelPrefix: options.labelPrefix || "quoteIndexSearch",
    builders: ({ collectionRef, maxResults }) => quoteSearchPlan(queryText).map(job => ({
      source: job.field, queryValue: job.value,
      queryRef: job.kind === "prefix" ? startsWithRangeQuery(collectionRef, job.field, job.value, maxResults)
        : job.kind === "token" ? arrayContainsQuery(collectionRef, job.field, job.value, maxResults)
        : firestoreQuery(collectionRef, where(job.field === "__name__" ? documentId() : job.field, "==", job.value), firestoreLimit(maxResults)),
    })),
  }).then(result => options.pagination
    ? { ...result, rawCount: result.records.length, records: result.records.filter(record => quoteSearchMatches(record, queryText)) }
    : result.filter(record => quoteSearchMatches(record, queryText)));

export const searchCustomers = (workspaceUid, queryText, options = {}) =>
  runSearchQueries({
    workspaceUid,
    collectionName: "customers",
    queryText,
    limit: options.limit,
    ttlMs: options.ttlMs || DEFAULT_TTL_MS,
    labelPrefix: options.labelPrefix || "customerSearchIndex",
    builders: ({ collectionRef, normalizedQuery, normalizedPhone, rawQuery, maxResults }) => {
      const isPhoneQuery = normalizedPhone.length >= 2 && /^\d+$/.test(String(rawQuery).replace(/\D+/g, ""));
      const jobs = [
        {
          source: isPhoneQuery ? "phonePrefixes" : "searchPrefixes",
          queryValue: isPhoneQuery ? normalizedPhone : normalizedQuery,
          queryRef: arrayContainsQuery(
            collectionRef,
            isPhoneQuery ? "phonePrefixes" : "searchPrefixes",
            isPhoneQuery ? normalizedPhone : normalizedQuery,
            maxResults
          ),
        },
        {
          source: "searchKeywords",
          queryValue: normalizedQuery,
          queryRef: arrayContainsQuery(collectionRef, "searchKeywords", normalizedQuery, maxResults),
        },
        {
          source: isPhoneQuery ? "normalizedPhone" : "normalizedName",
          queryValue: isPhoneQuery ? normalizedPhone : normalizedQuery,
          queryRef: startsWithRangeQuery(
            collectionRef,
            isPhoneQuery ? "normalizedPhone" : "normalizedName",
            isPhoneQuery ? normalizedPhone : normalizedQuery,
            maxResults
          ),
        },
        {
          source: "customerNameLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "customerNameLower", normalizedQuery, maxResults),
        },
        {
          source: "nameLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "nameLower", normalizedQuery, maxResults),
        },
      ];

      if (isPhoneQuery) {
        jobs.push({
          source: "normalizedWhatsapp",
          queryValue: normalizedPhone,
          queryRef: startsWithRangeQuery(collectionRef, "normalizedWhatsapp", normalizedPhone, maxResults),
        });
      }

      return jobs;
    },
  });

export const searchCustomerPeople = (workspaceUid, queryText, options = {}) =>
  runSearchQueries({
    workspaceUid,
    collectionName: CUSTOMER_SEARCH_INDEX_COLLECTION,
    queryText,
    limit: options.limit || 15,
    ttlMs: options.ttlMs || DEFAULT_TTL_MS,
    labelPrefix: options.labelPrefix || "customerPeopleSearch",
    builders: ({ collectionRef, normalizedQuery, normalizedPhone, rawQuery, maxResults }) => {
      const digitQuery = String(rawQuery || "").replace(/\D+/g, "");
      const isPhoneQuery = normalizedPhone.length >= 2 && digitQuery.length >= 2;
      const normalizedEmailQuery = normalizeEmailKey(rawQuery);
      const isEmailQuery =
        normalizedEmailQuery.length >= 2 && /[@.]/.test(String(rawQuery || ""));
      const jobs = [
        {
          source: isPhoneQuery ? "phonePrefixes" : "searchPrefixes",
          queryValue: isPhoneQuery ? normalizedPhone : normalizedQuery,
          queryRef: arrayContainsQuery(
            collectionRef,
            isPhoneQuery ? "phonePrefixes" : "searchPrefixes",
            isPhoneQuery ? normalizedPhone : normalizedQuery,
            maxResults
          ),
        },
        {
          source: "searchKeywords",
          queryValue: normalizedQuery,
          queryRef: arrayContainsQuery(collectionRef, "searchKeywords", normalizedQuery, maxResults),
        },
        {
          source: "searchTokens",
          queryValue: normalizedQuery,
          queryRef: arrayContainsQuery(collectionRef, "searchTokens", normalizedQuery, maxResults),
        },
        {
          source: isPhoneQuery ? "normalizedPhone" : "displayNameLower",
          queryValue: isPhoneQuery ? normalizedPhone : normalizedQuery,
          queryRef: startsWithRangeQuery(
            collectionRef,
            isPhoneQuery ? "normalizedPhone" : "displayNameLower",
            isPhoneQuery ? normalizedPhone : normalizedQuery,
            maxResults
          ),
        },
        {
          source: "customerNameLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "customerNameLower", normalizedQuery, maxResults),
        },
      ];

      if (isPhoneQuery) {
        jobs.push({
          source: "normalizedWhatsapp",
          queryValue: normalizedPhone,
          queryRef: startsWithRangeQuery(collectionRef, "normalizedWhatsapp", normalizedPhone, maxResults),
        });
      }

      if (isEmailQuery) {
        ["emailCanonical", "normalizedEmail", "emailLower"].forEach((fieldName) => {
          jobs.push({
            source: fieldName,
            queryValue: normalizedEmailQuery,
            queryRef: startsWithRangeQuery(collectionRef, fieldName, normalizedEmailQuery, maxResults),
          });
        });
      }

      return jobs;
    },
  });

export const searchCrmLeads = (workspaceUid, queryText, options = {}) =>
  runSearchQueries({
    workspaceUid,
    collectionName: "crm_leads",
    queryText,
    limit: options.limit,
    ttlMs: options.ttlMs || DEFAULT_TTL_MS,
    labelPrefix: options.labelPrefix || "crmLeadSearchIndex",
    pagination: options.pagination,
    builders: ({ collectionRef, normalizedQuery, compactQuery, normalizedPhone, rawQuery, maxResults }) => {
      const jobs = [
        {
          source: "quoteNo",
          queryValue: rawQuery,
          queryRef: startsWithRangeQuery(collectionRef, "quoteNo", rawQuery, maxResults),
        },
        {
          source: "primaryQuoteNo",
          queryValue: rawQuery,
          queryRef: startsWithRangeQuery(collectionRef, "primaryQuoteNo", rawQuery, maxResults),
        },
        {
          source: "customerNameLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "customerNameLower", normalizedQuery, maxResults),
        },
        {
          source: "projectNameLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "projectNameLower", normalizedQuery, maxResults),
        },
        {
          source: "stageLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "stageLower", normalizedQuery, maxResults),
        },
        {
          source: "sourceLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "sourceLower", normalizedQuery, maxResults),
        },
        {
          source: "searchKeywords",
          queryValue: normalizedQuery,
          queryRef: arrayContainsQuery(collectionRef, "searchKeywords", normalizedQuery, maxResults),
        },
        {
          source: "searchTokens",
          queryValue: normalizedQuery,
          queryRef: arrayContainsQuery(collectionRef, "searchTokens", normalizedQuery, maxResults),
        },
      ];

      if (/^\d+$/.test(compactQuery) && compactQuery.length >= 2) {
        jobs.push({
          source: "normalizedPhone",
          queryValue: normalizedPhone || compactQuery,
          queryRef: startsWithRangeQuery(
            collectionRef,
            "normalizedPhone",
            normalizedPhone || compactQuery,
            maxResults
          ),
        });
      }

      return jobs;
    },
  });

export const searchExecutionTemplates = (workspaceUid, queryText, options = {}) =>
  runSearchQueries({
    workspaceUid,
    collectionName: "execution_templates",
    queryText,
    limit: options.limit || 20,
    ttlMs: options.ttlMs || DEFAULT_TTL_MS,
    labelPrefix: options.labelPrefix || "globalSearch.executionTemplates",
    builders: ({ collectionRef, normalizedQuery, rawQuery, maxResults }) => [
      {
        source: "searchKeywords",
        queryValue: normalizedQuery,
        queryRef: arrayContainsQuery(collectionRef, "searchKeywords", normalizedQuery, maxResults),
      },
      {
        source: "nameLower",
        queryValue: normalizedQuery,
        queryRef: startsWithRangeQuery(collectionRef, "nameLower", normalizedQuery, maxResults),
      },
      {
        source: "itemTypeLower",
        queryValue: normalizedQuery,
        queryRef: startsWithRangeQuery(collectionRef, "itemTypeLower", normalizedQuery, maxResults),
      },
      {
        source: "documentId",
        queryValue: rawQuery,
        queryRef: firestoreQuery(
          collectionRef,
          orderBy(documentId()),
          startAt(rawQuery),
          endAt(`${rawQuery}\uf8ff`),
          firestoreLimit(maxResults)
        ),
      },
    ],
  });

export const searchTaskHistory = (workspaceUid, queryText, options = {}) =>
  runSearchQueries({
    workspaceUid,
    collectionName: "task_history",
    queryText,
    limit: options.limit || 20,
    ttlMs: options.ttlMs || DEFAULT_TTL_MS,
    labelPrefix: options.labelPrefix || "globalSearch.taskHistory",
    builders: ({ collectionRef, normalizedQuery, compactQuery, rawQuery, maxResults }) => {
      const jobs = [
        {
          source: "searchKeywords",
          queryValue: normalizedQuery,
          queryRef: arrayContainsQuery(collectionRef, "searchKeywords", normalizedQuery, maxResults),
        },
        {
          source: "customerNameLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "customerNameLower", normalizedQuery, maxResults),
        },
        {
          source: "taskTitleLower",
          queryValue: normalizedQuery,
          queryRef: startsWithRangeQuery(collectionRef, "taskTitleLower", normalizedQuery, maxResults),
        },
        {
          source: "quoteNo",
          queryValue: rawQuery,
          queryRef: startsWithRangeQuery(collectionRef, "quoteNo", rawQuery, maxResults),
        },
      ];

      if (/^\d+$/.test(compactQuery) && compactQuery.length >= 2) {
        jobs.push({
          source: "normalizedPhone",
          queryValue: compactQuery,
          queryRef: startsWithRangeQuery(collectionRef, "normalizedPhone", compactQuery, maxResults),
        });
      }

      return jobs;
    },
  });

export const buildSearchTokens = (record = {}) => {
  const values = Object.values(record)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value) => ["string", "number"].includes(typeof value))
    .map(normalizeSearchText)
    .join(" ");

  return Array.from(
    new Set(
      values
        .split(/\s+/)
        .map((token) => token.trim())
        .filter((token) => token.length >= 2)
        .map((token) => token.slice(0, 60))
    )
  ).slice(0, 100);
};
